import { useEffect, useState, useCallback } from "react";
import CreatableSelect from "react-select/creatable";
import {
    dieMasterAPI,
    supplierDieAPI,
    DiePurchaseOrderAPI,
} from "../../utlis/apiService";
import { notify } from "../../common/Toast";
import InputField from "../../common/InputField";
import { AnimatePresence, motion } from "framer-motion";
import {
    MdClose,
    MdOutlineSettings,
    MdDeleteOutline,
    MdAdd,
    MdAttachFile
} from "react-icons/md";
import Button from "../../common/Button";
import { FaPaperPlane, FaEye } from "react-icons/fa";
import { IoMdClose } from "react-icons/io";
import { HiOutlineCheckCircle } from "react-icons/hi";

const DiePurchaseOrderForm = ({ initialData, onSubmit, onCancel, isEditing, isLoading }) => {
    const today = new Date().toISOString()?.split("T")[0];
    const user = JSON.parse(sessionStorage.getItem("user"));
    const userId = user?.userId;

    const [formData, setFormData] = useState({
        po_No: 0,
        po_Date: today,
        supplier_Id: "",
        delivery_Days: "",
        delivery_Date: "",
        created_By: userId,
        details: [],
    });

    const [supplierOptions, setSupplierOptions] = useState([]);
    const [dieOptions, setDieOptions] = useState([]);

    useEffect(() => {
        const loadDropdowns = async () => {
            try {
                const [suppliers, dies] = await Promise.all([
                    supplierDieAPI.getAll(),
                    dieMasterAPI.getAll(),
                ]);

                if (suppliers?.data) {
                    setSupplierOptions(
                        suppliers.data.map((s) => ({
                            value: s.supplier_Id,
                            label: s.supplier_Name,
                        }))
                    );
                }

                if (dies?.data) {
                    setDieOptions(
                        dies.data.map((d) => ({
                            value: d.dieNumber,
                            label: d.dieNumber,
                        }))
                    );
                }
            } catch (err) {
                notify("Failed to load dropdowns", "error");
            }
        };
        loadDropdowns();
    }, []);

    useEffect(() => {
        if (initialData) {
            const dataToUse = initialData.data || initialData;
            const activeDetails = dataToUse.details || [];
            setFormData({
                ...dataToUse,
                po_No: dataToUse.po_No || dataToUse.PO_No || dataToUse.pO_No || 0,
                po_Date: dataToUse.po_Date ? dataToUse.po_Date.split('T')[0] : today,
                delivery_Date: dataToUse.delivery_Date ? dataToUse.delivery_Date.split('T')[0] : '',
                details: activeDetails.length > 0 ? activeDetails.map(d => {
                    const wh = d.width_Height || d.Width_Height || "";
                    const [w, h] = wh.split('X');
                    return {
                        ...d,
                        trn_No: d.trn_No ?? d.Trn_No ?? 0,
                        dieNumber: d.dieNumber ?? d.DieNumber ?? "",
                        subDieNo: d.subDieNo ?? d.SubDieNo ?? "",
                        width: w || "",
                        height: h || "",
                    };
                }) : [{
                    trn_No: 0,
                    dieNumber: "",
                    subDieNo: "",
                    is_Bloster: false,
                    bloster_No: "",
                    file: null,
                    is_Pdf_Upload: false,
                    orderWeight: "",
                    profile_Name: "",
                    series_Name: "",
                    width: "",
                    height: "",
                    die_Image: "",
                    imageFile: null,
                    is_Image_Upload: false,
                    range_Per: "",
                    area_Sqmm: "",
                    perimeter: ""
                }]
            });
        }
    }, [initialData]);

    const handleMasterChange = (field, value) => {
        setFormData((prev) => {
            const next = { ...prev, [field]: value };

            if (field === "po_Date" || field === "delivery_Days") {
                const pDate = field === "po_Date" ? value : prev.po_Date;
                const dDays = field === "delivery_Days" ? value : prev.delivery_Days;

                if (pDate && dDays) {
                    const date = new Date(pDate);
                    date.setDate(date.getDate() + parseInt(dDays));
                    next.delivery_Date = date.toISOString().split('T')[0];
                }
            }
            return next;
        });
    };

    const handleDetailChange = (index, field, value) => {
        setFormData((prev) => {
            const newDetails = [...prev.details];
            newDetails[index] = { ...newDetails[index], [field]: value };

            if (field === "is_Bloster" && !value) {
                newDetails[index].bloster_No = "";
            }
            return { ...prev, details: newDetails };
        });
    };

    const fetchSubDieNo = useCallback(async (dieNo, index) => {
        if (!dieNo) return;
        try {
            const res = await DiePurchaseOrderAPI.getNewSubDieNo(dieNo);
            const subDieNo = res?.subDieNo ?? "";
            setFormData((prev) => {
                const newDetails = [...prev.details];
                newDetails[index] = { ...newDetails[index], subDieNo: subDieNo };
                return { ...prev, details: newDetails };
            });
        } catch (err) {
            console.error("Failed to fetch sub die no", err);
        }
    }, []);

    const handleDieChange = (index, dieNumber) => {
        if (dieNumber) {
            const isDuplicate = formData.details.some((item, i) => i !== index && item.dieNumber === dieNumber);
            if (isDuplicate) {
                notify(`Die Number ${dieNumber} is already added to this order`, "warning");
                return;
            }
        }

        handleDetailChange(index, "dieNumber", dieNumber);
        if (dieNumber) {
            fetchSubDieNo(dieNumber, index);
        } else {
            handleDetailChange(index, "subDieNo", "");
        }
    };

    const handleAddRow = () => {
        setFormData((prev) => ({
            ...prev,
            details: [
                ...prev.details,
                {
                    trn_No: 0,
                    dieNumber: "",
                    subDieNo: "",
                    is_Bloster: false,
                    bloster_No: "",
                    file: null,
                    is_Pdf_Upload: false,
                    orderWeight: "",
                    profile_Name: "",
                    series_Name: "",
                    width: "",
                    height: "",
                    die_Image: "",
                    imageFile: null,
                    is_Image_Upload: false,
                    range_Per: "",
                    area_Sqmm: "",
                    perimeter: ""
                },
            ],
        }));
    };

    const handleRemoveRow = async (index) => {
        if (formData.details.length === 1) {
            notify("At least one item is required", "warning");
            return;
        }

        const itemToRemove = formData.details[index];
        if (itemToRemove.trn_No > 0) {
            try {
                await DiePurchaseOrderAPI.deleteDetail(formData.po_No, itemToRemove.trn_No);
                notify("Item deleted successfully", "success");
                setFormData((prev) => ({
                    ...prev,
                    details: prev.details.filter((_, i) => i !== index)
                }));
            } catch (error) {
                notify("Failed to delete item", "error");
            }
        } else {
            setFormData((prev) => ({
                ...prev,
                details: prev.details.filter((_, i) => i !== index)
            }));
        }
    };

    const handleSave = () => {
        const data = new FormData();
        const poNoToSend = formData.po_No ? formData.po_No : 0;
        data.append("PO_No", poNoToSend);
        data.append("PO_Date", formData.po_Date);
        data.append("Supplier_Id", formData.supplier_Id);
        data.append("Delivery_Days", formData.delivery_Days);
        data.append("Delivery_Date", formData.delivery_Date);
        data.append("Created_By", formData.created_By);

        formData.details.forEach((item, index) => {
            data.append(`Details[${index}].Trn_No`, item.trn_No || 0);
            data.append(`Details[${index}].DieNumber`, item.dieNumber);
            data.append(`Details[${index}].SubDieNo`, item.subDieNo || "");
            data.append(`Details[${index}].Is_Bloster`, item.is_Bloster || false);
            if (item.is_Bloster) {
                data.append(`Details[${index}].Bloster_No`, item.bloster_No);
            }

            // Send Is_Pdf_Upload flag to ensure backend knows a file exists/should be kept
            data.append(`Details[${index}].Is_Pdf_Upload`, item.is_Pdf_Upload || false);

            if (item.file instanceof File) {
                data.append(`Details[${index}].File`, item.file);
            }

            data.append(`Details[${index}].OrderWeight`, item.orderWeight || "");
            data.append(`Details[${index}].Profile_Name`, item.profile_Name || "");
            data.append(`Details[${index}].Series_Name`, item.series_Name || "");
            const combinedWH = (item.width || item.height) ? `${item.width || ""}X${item.height || ""}` : "";
            data.append(`Details[${index}].Width_Height`, combinedWH);
            data.append(`Details[${index}].Die_Image`, item.die_Image || "");
            data.append(`Details[${index}].Range_Per`, item.range_Per || "");
            data.append(`Details[${index}].Area_Sqmm`, item.area_Sqmm || "");
            data.append(`Details[${index}].Perimeter`, item.perimeter || "");

            data.append(`Details[${index}].Is_Image_Upload`, item.is_Image_Upload || false);

            if (item.imageFile instanceof File) {
                data.append(`Details[${index}].ImageFile`, item.imageFile);
            }
        });

        onSubmit(data);
    };

    return (
        <AnimatePresence>
            <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-2 z-[9999]">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 20 }}
                    className="bg-white rounded-2xl shadow-2xl w-full max-w-[95vw] max-h-[98vh] overflow-hidden flex flex-col"
                >
                    {/* Header */}
                    <div className="bg-gradient-to-r from-primary to-blue-700 text-white px-6 py-4 flex justify-between items-center shadow-lg">
                        <div className="flex items-center gap-3">
                            <div className="bg-white/20 p-2 rounded-lg">
                                <MdOutlineSettings className="text-white text-xl" />
                            </div>
                            <h2 className="text-xl font-bold text-white tracking-wide">
                                {isEditing ? "UPDATE PURCHASE ORDER" : "CREATE PURCHASE ORDER"}
                            </h2>
                        </div>
                        <button
                            onClick={onCancel}
                            className="hover:bg-white/20 p-2 rounded-full transition-colors"
                        >
                            <MdClose size={28} />
                        </button>
                    </div>

                    <div className="overflow-y-auto p-6 bg-slate-50/50 flex-1">
                        <div className="mb-6 bg-white p-4 rounded-xl shadow-sm border border-slate-100">
                            <h3 className="text-sm font-bold text-slate-500 uppercase mb-4 border-b pb-2">
                                PO Details
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-10 gap-4">
                                <div className="md:col-span-1">
                                    <InputField
                                        label="PO No"
                                        value={formData.po_No}
                                        readOnly
                                        placeholder="Auto"
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <InputField
                                        type="date"
                                        label="PO Date"
                                        value={formData.po_Date}
                                        onChange={(v) => handleMasterChange("po_Date", v)}
                                    />
                                </div>
                                <div className="md:col-span-1">
                                    <InputField
                                        label="Days"
                                        type="number"
                                        value={formData.delivery_Days}
                                        onChange={(v) => handleMasterChange("delivery_Days", v)}
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <InputField
                                        type="date"
                                        label="Del. Date"
                                        value={formData.delivery_Date}
                                        readOnly
                                    />
                                </div>
                                <div className="md:col-span-4">
                                    <InputField
                                        type="select"
                                        label="Supplier"
                                        value={formData.supplier_Id}
                                        options={supplierOptions}
                                        onChange={(s) => handleMasterChange("supplier_Id", s)}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Details Section */}
                        <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-100">
                            {/* Details Section - Refactored to Card Layout */}
                            <div className="bg-transparent space-y-4">
                                <div className="flex justify-between items-center px-1 mb-2">
                                    <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                                        Order Items <span className="bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full text-[10px]">{formData.details.length}</span>
                                    </h3>
                                </div>

                                <div className="space-y-4">
                                    {formData.details.map((item, index) => (
                                        <motion.div
                                            key={index}
                                            initial={{ opacity: 0, y: 15 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden group"
                                        >
                                            {/* Status Strip */}
                                            <div className="absolute top-0 left-0 w-1.5 h-full bg-blue-500 group-hover:bg-blue-600 transition-colors" />

                                            <div className="p-5">
                                                {/* Row 1: Primary Identity & PDF (Shifted Left) */}
                                                <div className="flex items-end gap-3 mb-6">
                                                    <div className="flex-none">
                                                        <div className="bg-blue-50 text-blue-600 w-10 h-10 rounded-xl flex items-center justify-center font-black text-xs border border-blue-100 shadow-sm">
                                                            {index + 1 < 10 ? `0${index + 1}` : index + 1}
                                                        </div>
                                                    </div>
                                                    <div className="w-[200px]">
                                                        <label className="text-[12px] font-medium text-slate-700 uppercase mb-1 py-1 block px-1">Die Number</label>
                                                        <CreatableSelect
                                                            options={dieOptions}
                                                            isDisabled={item.is_Inwarded}
                                                            value={item.dieNumber ? { value: item.dieNumber, label: item.dieNumber } : null}
                                                            onChange={(selected) => handleDieChange(index, selected?.value || "")}
                                                            onCreateOption={(inputValue) => handleDieChange(index, inputValue)}
                                                            placeholder="Die No..."
                                                            isClearable
                                                            formatCreateLabel={(inputValue) => `+ "${inputValue}"`}
                                                            menuPortalTarget={document.body}
                                                            styles={{
                                                                control: (base) => ({
                                                                    ...base,
                                                                    minHeight: '38px',
                                                                    fontSize: '13px',
                                                                    borderRadius: '8px',
                                                                    borderColor: '#e2e8f0',
                                                                    backgroundColor: item.is_Inwarded ? '#f1f5f9' : 'white'
                                                                }),
                                                                menuPortal: (base) => ({ ...base, zIndex: 9999 })
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="w-[150px]">
                                                        <label className="text-[12px] font-medium text-slate-700 uppercase mb-1 py-1 block px-1 text-center">Sub Die#</label>
                                                        <input type="text" value={item.subDieNo} readOnly className={`w-full ${item.is_Inwarded ? 'bg-slate-200' : 'bg-slate-50'} border border-slate-200 rounded-lg px-2 py-2 text-xs font-bold text-center text-slate-500 h-[38px]`} placeholder="Auto" />
                                                    </div>
                                                    <div className="flex-1 min-w-[150px]">
                                                        <InputField label="Profile Name" value={item.profile_Name} onChange={(v) => handleDetailChange(index, "profile_Name", v)} placeholder="Profile" className="h-[38px] text-xs" readOnly={item.is_Inwarded} />
                                                    </div>
                                                    <div className="flex-1 min-w-[150px]">
                                                        <InputField label="Series Name" value={item.series_Name} onChange={(v) => handleDetailChange(index, "series_Name", v)} placeholder="Series" className="h-[38px] text-xs" readOnly={item.is_Inwarded} />
                                                    </div>

                                                    {/* Increased width of PDF */}
                                                    <div className="w-[350px]">
                                                        <label className="text-[12px] font-medium text-slate-700 uppercase mb-1 py-1 block px-1">PDF Drawing</label>
                                                        <div className="flex items-center gap-2 h-[38px]">
                                                            <label className={`flex-1 flex items-center justify-center ${item.is_Inwarded ? 'bg-slate-100 cursor-not-allowed' : 'bg-white cursor-pointer hover:bg-white/80'} border border-slate-200 border-dashed rounded-md h-full transition-colors`}>
                                                                <MdAttachFile className="text-slate-400 text-lg mr-2" />
                                                                <span className="text-[13px] font-bold text-slate-500 truncate px-1">{item.is_Inwarded ? "LOCKED" : item.file ? "Change PDF" : "Choose PDF Drawing"}</span>
                                                                {!item.is_Inwarded && <input type="file" className="hidden" accept=".pdf" onChange={(e) => handleDetailChange(index, "file", e.target.files[0])} />}
                                                            </label>
                                                            {item.is_Pdf_Upload && !item.file && (
                                                                <a href={`https://hline.blob.core.windows.net/hlinefiles/Extrusion/DiePoPDFFiles/${formData.po_No}_${item.dieNumber}${item.subDieNo ? '_' + item.subDieNo : ''}.pdf`} target="_blank" rel="noopener noreferrer" className="bg-blue-600 text-white p-2 rounded-lg h-[38px] flex items-center shadow-sm hover:bg-blue-700 transition-colors" title="View PDF">
                                                                    <FaEye size={18} />
                                                                </a>
                                                            )}
                                                        </div>
                                                        {item.file && (
                                                            <div className="flex items-center gap-1 mt-1">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => window.open(URL.createObjectURL(item.file), '_blank')}
                                                                    className="text-[10px] text-blue-600 hover:text-blue-800 underline truncate font-medium text-left"
                                                                    title="Click to preview"
                                                                >
                                                                    {item.file.name}
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleDetailChange(index, "file", null)}
                                                                    className="text-red-500 hover:text-red-700 p-0.5 flex-none"
                                                                    title="Remove file"
                                                                >
                                                                    <IoMdClose size={12} />
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>

                                                     <div className="flex-none ml-auto mb-1">
                                                        {item.is_Inwarded ? (
                                                            <div className="bg-green-100 text-green-700 px-3 py-1.5 rounded-lg text-[10px] font-black tracking-widest border border-green-200 shadow-sm flex items-center gap-1">
                                                                <HiOutlineCheckCircle size={14} /> INWARDED
                                                            </div>
                                                        ) : (
                                                            <button onClick={() => handleRemoveRow(index)} className="bg-red-50 text-red-500 hover:bg-red-500 hover:text-white p-2 rounded-lg transition-all active:scale-95" title="Remove Item">
                                                                <MdDeleteOutline size={20} />
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Row 2: Parameters, Bloster & Image (Uniform Alignment) */}
                                                <div className="flex items-start gap-3 mb-4 border-t border-slate-50 pt-4">
                                                     <div className="w-[150px]">
                                                        <InputField label="Order Wt" type="number" value={item.orderWeight} onChange={(v) => handleDetailChange(index, "orderWeight", v)} placeholder="0.00" className="text-xs" readOnly={item.is_Inwarded} />
                                                    </div>
                                                    <div className="w-[250px] flex gap-1">
                                                        <div className="flex-1">
                                                            <InputField label="Width" type="number" value={item.width} onChange={(v) => handleDetailChange(index, "width", v)} placeholder="W" className="text-xs" readOnly={item.is_Inwarded} />
                                                        </div>
                                                        <div className="flex-1">
                                                            <InputField label="Height" type="number" value={item.height} onChange={(v) => handleDetailChange(index, "height", v)} placeholder="H" className="text-xs" readOnly={item.is_Inwarded} />
                                                        </div>
                                                    </div>
                                                    <div className="w-[150px]">
                                                        <InputField label="Area" type="number" value={item.area_Sqmm} onChange={(v) => handleDetailChange(index, "area_Sqmm", v)} placeholder="Area" className="text-xs" readOnly={item.is_Inwarded} />
                                                    </div>
                                                    <div className="w-[140px]">
                                                        <InputField label="Range %" type="number" value={item.range_Per} onChange={(v) => handleDetailChange(index, "range_Per", v)} placeholder="%" className="text-xs" readOnly={item.is_Inwarded} />
                                                    </div>
                                                    <div className="flex-1 min-w-[100px]">
                                                        <InputField label="Perimeter" type="number" value={item.perimeter} onChange={(v) => handleDetailChange(index, "perimeter", v)} placeholder="Perimeter" className="text-xs" readOnly={item.is_Inwarded} />
                                                    </div>

                                                    {/* Bloster Section - Perfectly Aligned */}
                                                     <div className="w-16 flex flex-col items-center">
                                                        <label htmlFor={`card_bloster_${index}`} className="text-[12px] font-medium text-slate-700 uppercase mb-1 py-1 cursor-pointer block w-full text-center">Bloster</label>
                                                        <div className="h-[38px] flex items-center justify-center">
                                                            <input
                                                                type="checkbox"
                                                                id={`card_bloster_${index}`}
                                                                disabled={item.is_Inwarded}
                                                                className={`w-5 h-5 text-blue-600 rounded border-slate-300 shadow-sm accent-blue-600 ${item.is_Inwarded ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                                                                checked={item.is_Bloster || false}
                                                                onChange={(e) => handleDetailChange(index, "is_Bloster", e.target.checked)}
                                                            />
                                                        </div>
                                                    </div>

                                                    {item.is_Bloster && (
                                                         <div className="flex-1 min-w-[130px]">
                                                            <InputField
                                                                value={item.bloster_No}
                                                                onChange={(v) => handleDetailChange(index, "bloster_No", v)}
                                                                placeholder="No"
                                                                className="h-[38px] text-[11px] mb-0"
                                                                label="Bloster#"
                                                                readOnly={item.is_Inwarded}
                                                            />
                                                        </div>
                                                    )}

                                                    {/* Image Upload - Aligned with PDF above */}
                                                    <div className="w-[350px] flex flex-col">
                                                        <label className="text-[12px] font-medium text-slate-700 uppercase mb-1 py-1 block px-1">Image</label>
                                                        <div className="flex items-center gap-2">
                                                             <label className={`w-full h-[38px] flex items-center justify-center ${item.is_Inwarded ? 'bg-slate-100 cursor-not-allowed' : 'bg-white cursor-pointer hover:bg-white/80'} border border-slate-200 border-dashed rounded-lg transition-colors px-2`}>
                                                                <MdAdd className="text-slate-400 text-lg flex-none" />
                                                                <span className="text-[13px] font-bold text-slate-500 truncate px-1">{item.is_Inwarded ? "LOCKED" : item.imageFile ? "Change IMG" : "Choose Die Image"}</span>
                                                                {!item.is_Inwarded && <input type="file" className="hidden" accept="image/*" onChange={(e) => handleDetailChange(index, "imageFile", e.target.files[0])} />}
                                                            </label>
                                                            {(item.die_Image || item.is_Image_Upload) && !item.imageFile && (
                                                                <a href={`https://hline.blob.core.windows.net/hlinefiles/Extrusion/DiePoImageFiles/${formData.po_No}_${item.dieNumber}${item.subDieNo ? '_' + item.subDieNo : ''}.jpg`} target="_blank" rel="noopener noreferrer" className="bg-blue-600 text-white p-2 rounded-lg h-[38px] flex items-center shadow-sm hover:bg-blue-700 transition-colors" title="View Image">
                                                                    <FaEye size={18} />
                                                                </a>
                                                            )}
                                                        </div>
                                                        {item.imageFile && (
                                                            <div className="flex items-center gap-1 mt-1">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => window.open(URL.createObjectURL(item.imageFile), '_blank')}
                                                                    className="text-[10px] text-blue-600 hover:text-blue-800 underline truncate font-medium text-left"
                                                                    title="Click to preview"
                                                                >
                                                                    {item.imageFile.name}
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleDetailChange(index, "imageFile", null)}
                                                                    className="text-red-500 hover:text-red-700 p-0.5 flex-none"
                                                                    title="Remove image"
                                                                >
                                                                    <IoMdClose size={12} />
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Spacer to match delete button in Row 1 */}
                                                    <div className="w-10 flex-none" />
                                                </div>
                                            </div>
                                        </motion.div>
                                    )) // End Map
                                    }

                                    {/* Add New Item Button at the bottom */}
                                    <div className="flex justify-center pt-2 pb-4">
                                        <button
                                            onClick={handleAddRow}
                                            className="group bg-blue-50 hover:bg-blue-600 text-blue-600 hover:text-white border-2 border-blue-100 hover:border-blue-600 border-dashed rounded-2xl px-8 py-3 font-black text-sm transition-all flex items-center gap-3 shadow-sm hover:shadow-xl hover:-translate-y-1 active:scale-95 duration-300"
                                        >
                                            <div className="bg-blue-100 group-hover:bg-blue-500 p-1.5 rounded-lg transition-colors">
                                                <MdAdd size={24} className="text-blue-600 group-hover:text-white" />
                                            </div>
                                            <span>ADD NEW ORDER ITEM</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="p-4 bg-slate-50 border-t flex justify-end gap-3">
                        <Button
                            text="Cancel"
                            onClick={onCancel}
                            className="flex-1 sm:flex-none bg-white hover:bg-slate-50 text-slate-600 border border-slate-300 px-4 py-2 rounded-lg font-bold transition-all flex items-center justify-center gap-2"
                            icon={IoMdClose}
                        />
                        <Button
                            text={isEditing ? "Update Order" : "Save order"}
                            onClick={handleSave}
                            icon={FaPaperPlane}
                            loading={isLoading}
                            loadingText={isEditing ? "Updating..." : "Saving..."}
                            className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-4 py-2 rounded-lg shadow-lg shadow-blue-200 transition-all flex items-center gap-2"
                        />
                    </div>

                </motion.div>
            </div>
        </AnimatePresence>
    );
};

export default DiePurchaseOrderForm;
